import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { HttpBusinessConflictError } from '~/api/errors';
import { useUpdatePassword } from '~/api/user';
import { Button } from '~/components';
import { Field, Form, Input } from '~/components/Form';
import { PasswordRequirements } from '~/features/account';
import {
  meetsPasswordMinLengthRequirement,
  meetsPasswordMixedCasingRequirement,
  meetsPasswordNonAlphaRequirement,
} from '~/utils/passwords';
import { toast } from '~/utils/toast';

import { actionsStyle, currentPasswordFieldStyle, formStyle, newPasswordFieldStyle } from './ChangePassword.css';

export const ChangePassword = () => {
  const mutation = useUpdatePassword();

  const { t } = useTranslation(['auth', 'common', 'settings', 'validation']);

  const {
    control,
    formState: { isSubmitted },
    handleSubmit,
    reset,
    setError,
  } = useForm({
    defaultValues: { currentPassword: '', newPassword: '', newPasswordConfirmation: '' },
  });

  const onSubmit = handleSubmit((data) => {
    mutation.mutate(data, {
      onSuccess: () => {
        toast.success({ description: t('settings:alerts.successUpdated') });
        reset();
      },
      onError: (error) => {
        if (error instanceof HttpBusinessConflictError && error.errorCode === 'CurrentPasswordIsIncorrect') {
          setError('currentPassword', { message: t('validation:password.incorrect') });
          return;
        }

        toast.danger({ description: t('common:error') });
      },
    });
  });

  return (
    <Form className={formStyle} onSubmit={onSubmit}>
      <Controller
        control={control}
        name="currentPassword"
        render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
          <Field.Root className={currentPasswordFieldStyle} invalid={invalid} name={name}>
            <Field.Label>{t('settings:fields.currentPassword.label')}</Field.Label>

            <Input autoComplete="current-password" onBlur={onBlur} onValueChange={onChange} ref={ref} type="password" value={value} />

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{
          required: { value: true, message: t('validation:required') },
          maxLength: {
            value: 255,
            message: t('validation:maxLength', { attribute: t('settings:fields.currentPassword.label'), max: 255 }),
          },
        }}
      />

      <Controller
        control={control}
        name="newPassword"
        render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
          <Field.Root className={newPasswordFieldStyle} dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
            <Field.Label>{t('settings:fields.newPassword.label')}</Field.Label>

            <Input autoComplete="new-password" onBlur={onBlur} onValueChange={onChange} ref={ref} type="password" value={value} />

            <Field.Footer>
              <Field.Error match={!!error}>{error?.message}</Field.Error>

              <PasswordRequirements password={value} showInvalid={isTouched || isSubmitted} />
            </Field.Footer>
          </Field.Root>
        )}
        rules={{
          required: { value: true, message: t('validation:required') },
          maxLength: { value: 255, message: t('validation:maxLength', { attribute: t('settings:fields.newPassword.label'), max: 255 }) },
          validate: (value) =>
            (meetsPasswordMinLengthRequirement(value) &&
              meetsPasswordMixedCasingRequirement(value) &&
              meetsPasswordNonAlphaRequirement(value)) ||
            t('auth:registrationPage.stepAccountInformation.form.passwordField.requirements.error'),
          deps: ['newPasswordConfirmation'],
        }}
      />

      <Controller
        control={control}
        name="newPasswordConfirmation"
        render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
          <Field.Root invalid={invalid} name={name}>
            <Field.Label>{t('settings:fields.newPasswordConfirmation.label')}</Field.Label>

            <Input autoComplete="new-password" onBlur={onBlur} onValueChange={onChange} ref={ref} type="password" value={value} />

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{
          required: { value: true, message: t('validation:required') },
          maxLength: {
            value: 255,
            message: t('validation:maxLength', { attribute: t('settings:fields.newPasswordConfirmation.label'), max: 255 }),
          },
          validate: (value, { newPassword }) => value === newPassword || t('validation:password.match'),
        }}
      />

      <div className={actionsStyle}>
        <Button icon="Save" loading={mutation.isPending} type="submit">
          {t('settings:profile.password.edit')}
        </Button>
      </div>
    </Form>
  );
};
